// On-device song byte cache (IndexedDB). First IDB user; raw API, no new dep.
// Every method degrades to a no-op / miss when IndexedDB is unavailable
// (private mode, old browser) so online play always works.

const DB = "drop-notes";
const STORE = "songs";

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB, 1);
    } catch {
      return resolve(null);
    }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

function tx<T>(
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve) => {
        if (!db) return resolve(undefined);
        try {
          const req = run(db.transaction(STORE, mode).objectStore(STORE));
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => resolve(undefined);
        } catch {
          resolve(undefined);
        }
      }),
  );
}

export async function getCached(id: string): Promise<ArrayBuffer | undefined> {
  const v = await tx<ArrayBuffer>("readonly", (s) => s.get(id) as IDBRequest<ArrayBuffer>);
  return v ?? undefined;
}

export async function putCached(id: string, bytes: ArrayBuffer): Promise<void> {
  await tx("readwrite", (s) => s.put(bytes, id) as IDBRequest<IDBValidKey>);
}

export async function removeCached(id: string): Promise<void> {
  await tx("readwrite", (s) => s.delete(id) as IDBRequest<undefined>);
}

export async function cachedIds(): Promise<Set<string>> {
  const keys = await tx<IDBValidKey[]>(
    "readonly",
    (s) => s.getAllKeys() as IDBRequest<IDBValidKey[]>,
  );
  return new Set((keys ?? []).map(String));
}
